import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

import { BadgeCheck } from 'lucide-react';

const About = () => {
    const videoRef = useRef(null);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.playbackRate = 0.5; // Set slow motion to 50% speed
        }
    }, []);

    const features = [
        { text: 'Architectural Expertise' },
        { text: 'Financing Network' },
        { text: 'Multiple Project Mandates' },
        { text: 'Legal & Compliance Support' }
    ];

    return (
        <section id="about" className="relative pt-80 sm:pt-48 pb-16 sm:pb-24 bg-white overflow-hidden">
            {/* Background decoration - updated to match new theme */}
            <div className="absolute inset-0 z-0 opacity-30">
                <div className="absolute right-0 bottom-0 top-0 w-1/3 bg-gray-50 rounded-l-[4rem]"></div>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="flex flex-col"
                    >
                        {/* 1. Chip heading */}
                        <div className="inline-block self-start px-5 py-2 mb-8 rounded-full bg-black shadow-sm order-1">
                            <span className="text-white text-sm font-black uppercase tracking-widest">About Us</span>
                        </div>

                        {/* 2. main section heading */}
                        <h3 className="text-3xl sm:text-4xl md:text-6xl font-sans font-bold text-primary leading-tight mb-6 sm:mb-8 order-2">
                            Your Trusted Partner in Mumbai Redevelopment
                        </h3>
                        <p className="text-lg sm:text-xl text-primary/80 leading-relaxed mb-8 sm:mb-10 font-sans font-medium order-2">
                            Excalibur Projects specializes in guiding housing societies through every step of redevelopment – from feasibility to handover. With deep expertise we match societies with right builders, manage projects flawlessly and protect the interest of stakeholders at every stage.
                        </p>

                        {/* 3. Video (Mobile Version) */}
                        <div className="block lg:hidden mb-12 order-3">
                            <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl group">
                                <div className="aspect-[4/5] bg-gray-200 relative">
                                    <video
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        className="absolute inset-0 w-full h-full object-cover"
                                        onLoadedData={(e) => e.target.playbackRate = 0.5}
                                    >
                                        <source src="/videos/about-video.mp4" type="video/mp4" />
                                    </video>
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                    <div className="absolute bottom-6 left-6 right-6 text-white text-center">
                                        <p className="font-sans font-medium text-xl mb-1">Modern Living</p>
                                        <p className="text-[11px] text-gray-200 font-sans">Transforming skylines, one society at a time.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 4. Features grid part */}
                        <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:gap-x-12 sm:gap-y-12 pt-8 sm:pt-10 border-t border-gray-100 order-4">
                            {features.map((feature, index) => (
                                <div key={index} className="flex flex-col gap-3">
                                    <div className="text-primary flex items-center gap-2">
                                        <BadgeCheck className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2} />
                                    </div>
                                    <h4 className="text-lg sm:text-2xl text-primary font-bold leading-tight max-w-[180px] sm:max-w-[260px]">
                                        {feature.text}
                                    </h4>
                                </div>
                            ))}
                        </div>

                        {/* 5. CTA */}
                        <div className="order-5">
                            <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="mt-10 sm:mt-12 w-full sm:w-auto px-10 h-14 flex items-center justify-center bg-logo-gradient hover-logo-gradient text-white font-bold rounded-2xl transition-all shadow-xl shadow-primary/10 whitespace-nowrap">
                                Contact Us Now
                            </button>
                        </div>
                    </motion.div>

                    {/* Desktop Video Column (Hidden on mobile) */}
                    <motion.div
                        className="hidden lg:block relative"
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        {/* Image Container with matching rounded aesthetic */}
                        <div className="relative rounded-[3rem] overflow-hidden shadow-2xl group">
                            <div className="aspect-[4/5] bg-gray-200 relative">
                                {/* Using a sourced image for 'About Us' as requested */}
                                {/* Local Video Implementation */}
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    poster="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&q=80"
                                >
                                    <source src="/videos/about-video.mp4" type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

                                <div className="absolute bottom-6 sm:bottom-8 left-6 sm:left-8 right-6 sm:right-8 text-white">
                                    <p className="font-sans font-medium text-xl sm:text-2xl mb-1 sm:mb-2">Modern Living</p>
                                    <p className="text-[11px] sm:text-sm text-gray-200 font-sans leading-tight">Transforming skylines, one society at a time.</p>
                                </div>
                            </div>
                        </div>

                        {/* Floating Badge */}
                        <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-3xl shadow-xl flex items-center gap-4 animate-bounce-slow">
                            <div className="bg-green-100 p-3 rounded-full text-green-600">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm font-bold text-darkText font-sans">RERA Compliant</p>
                                <p className="text-xs text-gray-400 font-sans">100% Verified</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default About;
